
    import {
      getAllInventoryReservations,
      createInventoryReservation,
      createAllInventoryReservations,
      updateInventoryReservation,
      releaseInventoryReservation,
      deleteInventoryReservation,
      getReservationSummary,
      getStaleReservations,
      getInventoryIssues,
      getRecentlyResolvedIssues,
      resolveIssue as resolveIssueApi,
      resolveWithInventory as resolveWithInventoryApi,
      resolveWithLocation as resolveWithLocationApi,
      resolveWithSurplus as resolveWithSurplusApi,
      markAsBackorder as markAsBackorderApi,
    } from "@/services/inventoryReservation";
    import { fetchAllPages } from "@/utils/fetchAllPages";

    const state = {
      inventoryReservations: [],
      processed: false,
      dashboardSummary: [],
      staleReservations: [],
      issues: [],
      issuesProcessed: false,
      recentlyResolved: [],
    };

    const getters = {
      getInventoryReservations(state) {
        return state.inventoryReservations;
      },
      inventoryReservationProcessed(state){
        return state.processed;
      }
    };

    const actions = {
      fetchInventoryReservations({ commit }, params) {
          commit("SET_PROCESSING");
          return new Promise((resolve, reject) => {
            getAllInventoryReservations(
              params
            )
              .then(response => {
                commit("SET_INVENTORYRESERVATIONS", response.data);
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        createInventoryReservation({ commit }, inventoryReservation) {
          return new Promise((resolve, reject) => {
            createInventoryReservation(inventoryReservation)
              .then(response => {
                commit("SET_PROCESSED");
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        createAllInventoryReservations({ commit }, inventoryReservations) {
          return new Promise((resolve, reject) => {
            createAllInventoryReservations(inventoryReservations)
              .then(response => {
                commit("SET_PROCESSED");
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        updateInventoryReservation({ commit }, inventoryReservation) {
          return new Promise((resolve, reject) => {
            updateInventoryReservation(inventoryReservation)
              .then(response => {
                commit("SET_PROCESSED");
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        releaseInventoryReservation({ commit }, {worksheetId, inventoryId, reservedQuantity, inventoryUnitId}) {
          return new Promise((resolve, reject) => {
            releaseInventoryReservation(worksheetId, inventoryId, reservedQuantity, inventoryUnitId)
              .then(response => {
                commit("SET_PROCESSED");
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        deleteInventoryReservation({ commit }, id) {
          return new Promise((resolve, reject) => {
            deleteInventoryReservation(id)
              .then(response => {
                commit("SET_PROCESSED");
                resolve(response.data);
              })
              .catch(error => {
                reject(error);
              });
          });
        },
        fetchDashboardSummary({ commit }) {
          return getReservationSummary().then(response => {
            commit("SET_DASHBOARD_SUMMARY", response.data);
            return response.data;
          });
        },
        fetchStaleReservations({ commit }, daysOld = 7) {
          return getStaleReservations(daysOld).then(response => {
            commit("SET_STALE_RESERVATIONS", response.data);
            return response.data;
          });
        },
        // Read EVERY page, not just the first.
        //
        // This screen splits one list into two tabs client-side (needsReviewItems /
        // backorderItems), so it needs the whole outstanding set, not a page of it.
        // It passed no page or size at all, so Spring applied its default of 20 and
        // returned HTTP 200 with a short list — nothing failed, the rest was simply
        // invisible.
        //
        // Bergsoe, 2026-09-22: Sureerat could not find the 15.6 kg and 9.0 kg
        // calcium-98% shortfalls on worksheet 107144. Both rows were present and
        // NEEDS_REVIEW, but ranked 45th and 46th of 59 by createTime DESC, i.e. on
        // page 3 of a screen that has no page 2. 39 of 59 issues were unreachable,
        // which is also why items from April were still sitting untriaged.
        //
        // fetchAllPages loops on the server's own totalPages, so a clamp cannot fool
        // it.
        //
        // No `sort` is passed deliberately. findByInventoryDecisionStatuses carries a
        // hardcoded `ORDER BY e.createTime DESC` inside the JPQL, and a Pageable sort
        // would be appended to it rather than replacing it. The ordering is therefore
        // the server's, not ours. That leaves the usual unstable-paging caveat -
        // createTime is not unique here - but this list is small (59 rows today, read
        // in one page of 200), so every row arrives in a single request and there is
        // no page boundary for a tie to fall across.
        fetchInventoryIssues({ commit }, params = { status: 'NEEDS_REVIEW,BACKORDER' }) {
          commit("SET_ISSUES_PROCESSING");
          return fetchAllPages(
            (pageParams) => getInventoryIssues(pageParams),
            { ...params }
          ).then(data => {
            commit("SET_ISSUES", data);
            return data;
          });
        },
        // Same defect as fetchInventoryIssues above: no page or size, so Spring's
        // default of 20 applied. A busy day of triage can resolve more than 20 issues,
        // and this is the tab an operator uses to confirm their work landed - a short
        // list here reads as "my fix did not save".
        fetchRecentlyResolved({ commit }, params = { hoursAgo: 24 }) {
          return fetchAllPages(
            (pageParams) => getRecentlyResolvedIssues(pageParams),
            { ...params }
          ).then(data => {
            commit("SET_RECENTLY_RESOLVED", data);
            return data;
          });
        },
        resolveIssue(_, { entryId, note }) {
          return resolveIssueApi(entryId, note);
        },
        resolveIssueWithInventory(_, { entryId, inventoryId }) {
          return resolveWithInventoryApi(entryId, inventoryId);
        },
        resolveIssueWithLocation(_, { entryId, locationId }) {
          return resolveWithLocationApi(entryId, locationId);
        },
        resolveIssueWithSurplus(_, { entryId, note }) {
          return resolveWithSurplusApi(entryId, note);
        },
        markIssueAsBackorder(_, entryId) {
          return markAsBackorderApi(entryId);
        },
      };

    const mutations = {
      SET_INVENTORYRESERVATIONS(state, data) {
        state.inventoryReservations = data;
        state.processed = true;
      },
      SET_PROCESSING(state) {
        state.processed = false;
      },
      SET_PROCESSED(state) {
        state.processed = true;
      },
      SET_DASHBOARD_SUMMARY(state, data) {
        state.dashboardSummary = data;
      },
      SET_STALE_RESERVATIONS(state, data) {
        state.staleReservations = data;
      },
      SET_ISSUES(state, data) {
        state.issues = data;
        state.issuesProcessed = true;
      },
      SET_ISSUES_PROCESSING(state) {
        state.issuesProcessed = false;
      },
      SET_RECENTLY_RESOLVED(state, data) {
        state.recentlyResolved = data;
      },
    };

    export default {
      state,
      getters,
      actions,
      mutations
    };
    